import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  InputAdornment,
  Alert,
  Paper,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CloudUpload } from '@mui/icons-material';
import { getServices, addService, updateService, type ServiceData } from '../../services/api';
import { uploadImage, getImageUrl } from '../../services/uploadService';

interface Service {
  id: number;
  name: string;
  description: string;
  duration: number;
  price: number;
  imageUrl?: string;
}

const ServiceManagement: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    duration: 30,
    price: 0,
    imageUrl: '',
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: 30,
    price: 0,
    imageUrl: '',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const data = await getServices();
      setServices(data);
    } catch (error) {
      setError('Failed to load services');
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setDialogOpen(false);
    setFormData({
      name: '',
      description: '',
      duration: 30,
      price: 0,
      imageUrl: '',
    });
    setImageFile(null);
    setImagePreview('');
    setEditingService(null);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let imageUrl = formData.imageUrl;
      
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      const serviceData: ServiceData = {
        name: formData.name,
        description: formData.description,
        duration: formData.duration,
        price: formData.price,
        imageUrl,
      };

      if (editingService) {
        await updateService(editingService.id, serviceData);
      } else {
        await addService(serviceData);
      }
      
      await fetchServices();
      handleClose();
    } catch (error) {
      setError('Failed to save service');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description,
      duration: service.duration,
      price: service.price,
      imageUrl: service.imageUrl || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      // Implement delete logic
    } catch (error) {
      setError('Failed to delete service');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection={{ xs: "column", sm: "row" }}
        textAlign="center"
        gap={2}
        mb={4}
        pt={{ xs: 2, sm: 1 }}
      >
        <Typography variant="h4" component="h1">
          Services+
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{
            backgroundColor: "primary.main",
            color: "text.light",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
        >
          Add New Service
        </Button>
      </Box>


      <Grid container spacing={3}>
        {services.map((service) => (
          <Grid item xs={12} sm={6} md={4} key={service.id}>
            <Paper sx={{ 
              p: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              background: (theme) => theme.palette.background.paper,
              backdropFilter: 'blur(20px)',
            }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ color: 'text.primary' }}
              >
                {service.name}
              </Typography>
              <Typography 
                variant="body2"
                sx={{ color: 'text.secondary', mb: 2 }}
              >
                {service.description}
              </Typography>
              <Typography 
                variant="body1"
                sx={{ color: 'text.primary' }}
              >
                Duration: {service.duration} minutes
              </Typography>
              <Typography 
                variant="h6"
                sx={{ 
                  color: 'primary.main',
                  mt: 'auto',
                  mb: 2 
                }}
              >
                ${service.price}
              </Typography>
              <Box sx={{ mt: 'auto' }}>
                <Button
                  variant="outlined"
                  onClick={() => handleEdit(service)}
                  fullWidth
                  sx={{
                    mb: 1,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'primary.light',
                    },
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleDelete(service.id)}
                  fullWidth
                  sx={{
                    backgroundColor: 'error.main',
                    color: 'text.light',
                    '&:hover': {
                      backgroundColor: 'error.dark',
                    },
                  }}
                >
                  Delete
                </Button>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Dialog 
        open={dialogOpen} 
        onClose={handleClose}
        onBackdropClick={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: (theme) => theme.palette.background.paper,
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <DialogTitle sx={{ color: 'text.primary' }}>
          {editingService ? 'Edit Service' : 'Add New Service'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            sx={{
              '& label': { color: 'text.secondary' },
              '& label.Mui-focused': { color: 'primary.main' },
            }}
          />
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            sx={{
              '& label': { color: 'text.secondary' },
              '& label.Mui-focused': { color: 'primary.main' },
            }}
          />
          <TextField
            fullWidth
            label="Duration"
            type="number"
            InputProps={{
              endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
            }}
            value={formData.duration}
            onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
            margin="normal"
            sx={{
              '& label': { color: 'text.secondary' },
              '& label.Mui-focused': { color: 'primary.main' },
            }}
          />
          <TextField
            fullWidth
            label="Price"
            type="number"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
            margin="normal"
            sx={{
              '& label': { color: 'text.secondary' },
              '& label.Mui-focused': { color: 'primary.main' },
            }}
          />
          <Box sx={{ mt: 2, mb: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload"
              type="file"
              onChange={handleImageChange}
            />
            <label htmlFor="image-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                startIcon={<CloudUpload />}
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'primary.light',
                  },
                }}
              >
                Upload Image
              </Button>
            </label>
            {(imagePreview || formData.imageUrl) && (
              <Box sx={{ mt: 2, position: 'relative' }}>
                <img
                  src={imagePreview || getImageUrl(formData.imageUrl)}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                    borderRadius: 4,
                  }}
                />
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                  }}
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview('');
                    setFormData({ ...formData, imageUrl: '' });
                  }}
                >
                  <DeleteIcon sx={{ color: 'white' }} />
                </IconButton>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClose}
            sx={{ 
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: 'primary.main',
              color: 'text.light',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            {loading ? 'Saving...' : (editingService ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ServiceManagement; 